/* eslint-disable react/no-unstable-nested-components, react/prop-types */
import { AreaChart } from '@mantine/charts';
import type { TSessionByASR } from '@newo/api/gateway';
import { useMemo } from 'react';
import dayjs from '@/libs/dayjs.ts';
// mantine
// components
import ChartTooltip from '@/pages/Report/components/ChartTooltip';
// utils
import { formatDate } from '@/utils/format.ts';
import { useIsDesktop } from '@/pages/Report/Report.hooks.ts';
import {
    CHART_HEIGHT_DESKTOP,
    CHART_HEIGHT_MOBILE
} from '@/pages/Report/Report.utils.ts';
import { cn } from '@/utils';

interface ASRAreaChartProps {
    data: TSessionByASR[];
}

const SUNDAY = 0;
const SATURDAY = 6;
const WIDTH_PER_CHARACTER = 8;
const LEFT_PADDING = 40;

// Function to check if a date is a weekend (Saturday or Sunday)
const isWeekend = (date: string) => {
    const day = dayjs(date).day();
    return day === SUNDAY || day === SATURDAY;
};

const ASRAreaChart = ({ data }: ASRAreaChartProps) => {
    const isDesktop = useIsDesktop();
    const h = isDesktop ? CHART_HEIGHT_DESKTOP : CHART_HEIGHT_MOBILE;

    const maxLabelLength = useMemo(() => {
        return Math.max(...data.map((item) => String(item.asr_amount).length));
    }, [data]);

    const estimatedWidth = maxLabelLength * WIDTH_PER_CHARACTER + LEFT_PADDING;

    return (
        <AreaChart
            areaProps={{
                isAnimationActive: true,
                stroke: '#12a251',
                strokeWidth: 2,
                fill: 'url(#asr_gradient)',
                dot: (props) => {
                    const { cx, cy, payload, key } = props;
                    const isWeekendDay = payload && isWeekend(payload.date);

                    return (
                        <circle
                            cx={cx}
                            cy={cy}
                            fill={isWeekendDay ? 'white' : '#12a251'}
                            key={key}
                            r={4}
                            stroke="#12a251"
                            strokeWidth={2}
                        />
                    );
                },
                activeDot: (props) => {
                    const { cx, cy, payload, key } = props;
                    const isWeekendDay = payload && isWeekend(payload.date);

                    return (
                        <circle
                            cx={cx}
                            cy={cy}
                            fill={isWeekendDay ? 'white' : '#f44f15'}
                            key={key}
                            r={4}
                            stroke="#f44f15"
                            strokeWidth={2}
                        />
                    );
                }
            }}
            curveType="monotone"
            data={data}
            dataKey="date"
            h={h}
            series={[{ name: 'asr_amount', color: 'green.5' }]}
            tickLine="y"
            tooltipProps={{
                wrapperStyle: { fontSize: '10px' },
                content: ({ label, payload }) => {
                    const dayName = label ? dayjs(label).format('dddd') : '';
                    const isWeekendDay = label ? isWeekend(label) : false;
                    return (
                        <ChartTooltip>
                            <div className="font-bold">
                                {label ? formatDate(label, 'MMM DD, YYYY') : ''}{' '}
                                <span
                                    className={cn({
                                        'text-orange-8': isWeekendDay
                                    })}
                                >
                                    ({dayName})
                                </span>
                            </div>
                            <div>{`Revenue: ${payload.at(0)?.value}$`}</div>
                        </ChartTooltip>
                    );
                }
            }}
            unit="$"
            withYAxis={isDesktop}
            xAxisLabel="Date"
            xAxisProps={{
                tickFormatter: (value) => formatDate(value ?? '', 'MM/DD')
            }}
            yAxisLabel="Revenue"
            yAxisProps={{ width: estimatedWidth }}
        >
            <defs>
                <linearGradient id="asr_gradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#12a251" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#12a251" stopOpacity={0} />
                </linearGradient>
            </defs>
        </AreaChart>
    );
};

export default ASRAreaChart;
